import { actionLoadScene, actionShortcuts } from "../../actions";
import { getShortcutFromShortcutName } from "../../actions/shortcuts";
import { useTunnels } from "../../context/tunnels";
import { useUIAppState } from "../../context/ui-appState";
import { t, useI18n } from "../../i18n";
import { useDevice, useExcalidrawActionManager } from "../App";
import { ExcalidrawLogo } from "../ExcalidrawLogo";
import { HelpIcon, LoadIcon, usersIcon } from "../icons";

import type { JSX } from "react";

const WelcomeScreenMenuItemContent = ({
  icon,
  shortcut,
  children,
}: {
  icon?: JSX.Element;
  shortcut?: string | null;
  children: React.ReactNode;
}) => {
  const device = useDevice();
  return (
    <>
      <div className="welcome-screen-menu-item__icon">{icon}</div>
      <div className="welcome-screen-menu-item__text">{children}</div>
      {shortcut && !device.editor.isMobile && (
        <div className="welcome-screen-menu-item__shortcut">{shortcut}</div>
      )}
    </>
  );
};
WelcomeScreenMenuItemContent.displayName = "WelcomeScreenMenuItemContent";

const WelcomeScreenMenuItem = ({
  onSelect,
  children,
  icon,
  shortcut,
  className = "",
  ...props
}: {
  onSelect: () => void;
  children: React.ReactNode;
  icon?: JSX.Element;
  shortcut?: string | null;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) => {
  return (
    <button
      {...props}
      type="button"
      className={`welcome-screen-menu-item ${className}`}
      onClick={onSelect}
    >
      <WelcomeScreenMenuItemContent icon={icon} shortcut={shortcut}>
        {children}
      </WelcomeScreenMenuItemContent>
    </button>
  );
};
WelcomeScreenMenuItem.displayName = "WelcomeScreenMenuItem";

const WelcomeScreenMenuItemLink = ({
  children,
  href,
  icon,
  shortcut,
  className = "",
  ...props
}: {
  children: React.ReactNode;
  href: string;
  icon?: JSX.Element;
  shortcut?: string | null;
} & React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
  return (
    <a
      {...props}
      className={`welcome-screen-menu-item ${className}`}
      href={href}
      target="_blank"
      rel="noopener"
    >
      <WelcomeScreenMenuItemContent icon={icon} shortcut={shortcut}>
        {children}
      </WelcomeScreenMenuItemContent>
    </a>
  );
};
WelcomeScreenMenuItemLink.displayName = "WelcomeScreenMenuItemLink";

const Center = ({ children }: { children?: React.ReactNode }) => {
  const { WelcomeScreenCenterTunnel } = useTunnels();
  return (
    <WelcomeScreenCenterTunnel.In>
      <div className="welcome-screen-center">
        {children || (
          <>
            <Logo />
            <Heading>{t("welcomeScreen.defaults.center_heading")}</Heading>
            <Menu>
              <MenuItemLoadScene />
              <MenuItemHelp />
            </Menu>
          </>
        )}
      </div>
    </WelcomeScreenCenterTunnel.In>
  );
};
Center.displayName = "Center";

const Logo = ({ children }: { children?: React.ReactNode }) => {
  return (
    <div className="welcome-screen-center__logo welcome-screen-decor">
      <svg
        id="opencanvas-name"
        data-name="opencanvas-name"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 764.71 258"
        fill="currentColor"
        style={{ width: "350px", height: "auto" }}
      >
        <path d="M106.74,79.58s-9.67,2.65-17.18,8.97c-18.62,15.67-20.49,55.26-4.48,75.78,18.77,24.05,51.89,6.79,69.74,23.18,13.04,11.97,13.08,47.48.17,59.42-12.62,11.67-47,11.45-58.67-1.82-14.64-16.64-.81-45.34-18.51-65.39-19.76-22.39-58.27-2.6-72.51-26.72-7.9-13.38-7.51-43.74,4.85-54.54,16.04-14.01,45.14-1.5,64.51-17.58,23.36-19.39,4.83-56.53,24.89-71.64,14.12-10.64,38.48-13.29,53.73-.17,17.47,15.03,16.59,44.98,3.92,58.37-6.12,6.47-14.83,8.9-17.74,9.71-13.01,3.62-19.6-1.16-32.72,2.44Z" />
        <path d="M295.37,134.55l11.1-2.81V10.64l-11.3.8v-3.01L328.87.2l1.21,1v27.31h1.01c5.85-15.06,15.54-28.31,34.7-28.31,20.18,0,31.68,16.27,31.68,45.38,0,37.15-18.97,54.42-41.77,54.42-10.09,0-18.97-3.21-24.82-9.24v40.97l10.9,2.81v3.01h-46.41v-3.01ZM371.64,48.2c0-27.11-6.66-35.14-16.75-35.14-11.7,0-19.98,11.05-24.01,28.52v42.77c4.24,4.42,10.49,8.03,17.96,8.03,15.34,0,22.8-15.26,22.8-44.18Z" />
        <path d="M195.2,50.21C195.2,20.77,213.15.2,243.19.2s46.18,19.16,46.18,49.61-16.94,50.01-46.58,50.01-47.59-20.17-47.59-49.61ZM263.56,51.43c0-27.83-7.06-45.78-21.98-45.78-14.12,0-20.57,15.93-20.57,41.95,0,28.23,7.46,46.99,21.98,46.99s20.57-17.75,20.57-43.16Z" />
        <path d="M406.14,51.02c0-32.47,19.97-51.02,49.81-51.02,24.6,0,38.92,15.53,35.29,43.56h-59.29v4.24c0,28.23,12.7,39.53,31.26,39.53,4.35,0,12.52-.25,17.48-5.75,6.68-7.41,2.81-19.23,8.74-21.47.37-.14,1.93-.73,3.03,0,3.6,2.42.36,18.07-8.42,27.82-10.44,11.6-26.11,12.1-30.71,12.1-30.45,0-47.19-19.96-47.19-49ZM469.46,39.12c1.61-15.93.4-34.49-12.91-34.49-14.72,0-22.39,15.53-24.2,34.49h37.11Z" />
        <path d="M498.71,94.38l10.89-2.82V10.49c7.53-3.5,15.06-6.99,22.59-10.49l1.01.81v27.63h1.21c4.43-12.1,16.74-28.44,38.52-28.44,24,0,25.01,21.78,25.01,36.1v55.46l10.89,2.82v3.02h-46.38v-3.02l11.09-2.82v-58.68c0-14.92-3.43-20.17-12.91-20.17-14.32,0-22.99,13.91-26.62,27.63v51.22l11.09,2.82v3.02h-46.38v-3.02Z" />
        <path d="M195.2,209c0-33.68,21.38-51.02,50.21-51.02,21.58,0,32.27,11.09,32.27,23.19,0,8.27-4.44,14.92-13.11,14.92-.9,0-3.06-.05-5.38-1.19-3.56-1.75-7.27-6-6.72-9.9.7-4.99,7.78-5.26,8.67-10.89.55-3.46-1.64-6.34-2.17-7.04-2.19-2.86-6.13-4.66-12.15-4.66-16.94,0-25.81,14.32-25.81,42.15s9.08,40.94,29.04,40.94c2.26,0,14.85-.06,20.5-8.19,4.95-7.12,1.78-16.42,6.72-18.42.4-.16,1.94-.72,3.03,0,3.08,2.04.46,12.99-3.35,19.79-6.91,12.32-22.15,19.34-35.57,19.34-25.81,0-46.18-16.13-46.18-49Z" />
        <path d="M376.44,253.78c-.46,2.8-7.77,4.22-13.46,4.22-12.7,0-18.96-8.27-19.97-18.35h-.61c-5.44,8.87-15.73,18.35-31.05,18.35-14.12,0-24.4-7.46-24.4-20.97,0-17.34,14.72-24,35.29-29.24,12.1-3.23,19.56-7.06,19.56-12.91v-4.03c0-20.17-6.05-27.43-18.76-27.43-9.88,0-16.13,5.24-16.13,13.31,0,5.65,3.43,9.88,8.27,10.69,0,4.84-5.04,11.29-13.51,11.29-7.26,0-11.7-5.65-11.7-13.51,0-14.52,17.54-27.22,39.53-27.22,23.39,0,36.7,11.29,36.7,34.89v41.54c.02,6.42,1.61,11.32,3.35,13.29,1.65,1.87,2.95,2.36,2.95,2.36,1.23.46,1.97.23,2.77.89.85.7,1.29,1.99,1.15,2.84ZM341.81,234.61v-31.26c-2.22,2.42-5.65,4.03-12.1,6.66-8.47,3.43-17.34,7.26-17.34,20.57,0,8.67,3.23,15.33,12.1,15.33,6.66,0,12.7-3.83,17.34-11.29Z" />
        <path d="M387.79,252.36l10.89-2.82v-81.07c7.53-3.5,15.06-6.99,22.59-10.49l1.01.81v27.63h1.21c4.43-12.1,16.74-28.44,38.52-28.44,24,0,25.01,21.78,25.01,36.1v55.46l10.89,2.82v3.02h-46.38v-3.02l11.09-2.82v-58.68c0-14.92-3.43-20.17-12.91-20.17-14.32,0-22.99,13.91-26.62,27.63v51.22l11.09,2.82v3.02h-46.38v-3.02Z" />
        <path d="M500.93,166.04l-9.08-2.42v-3.02h48.2v3.02l-12.71,2.42,23.39,64.33,24-64.33-11.9-2.42v-3.02h27.42v3.02l-9.07,2.42-34.89,90.14h-9.68l-35.69-90.14Z" />
        <path d="M747.37,238.44c0-20.77-54.85-23.39-54.85-52.03,0-15.53,13.51-28.44,38.12-28.44,20.97,0,31.26,9.88,31.26,20.97,0,7.26-4.23,12.7-12.5,12.7-5.65,0-11.09-3.23-12.5-7.86,4.03-1.61,7.66-4.64,7.66-9.68,0-6.86-6.66-11.7-16.94-11.7-10.89,0-18.35,4.84-18.35,12.91,0,23.39,55.46,21.17,55.46,53.84,0,14.92-13.91,28.84-41.34,28.84-23.8,0-34.08-11.29-34.08-21.98,0-8.27,5.24-14.72,12.91-14.72,6.25,0,9.68,3.23,10.89,8.67-5.04,2.42-7.26,6.05-7.26,10.28,0,6.66,4.84,13.31,20.37,13.31,14.52,0,21.17-7.46,21.17-15.12Z" />
        <path d="M677.13,253.78c-.46,2.8-7.77,4.22-13.46,4.22-12.7,0-18.96-8.27-19.97-18.35h-.61c-5.44,8.87-15.73,18.35-31.05,18.35-14.12,0-24.4-7.46-24.4-20.97,0-17.34,14.72-24,35.29-29.24,12.1-3.23,19.56-7.06,19.56-12.91v-4.03c0-20.17-6.05-27.43-18.76-27.43-9.88,0-16.13,5.24-16.13,13.31,0,5.65,3.43,9.88,8.27,10.69,0,4.84-5.04,11.29-13.51,11.29-7.26,0-11.7-5.65-11.7-13.51,0-14.52,17.54-27.22,39.53-27.22,23.39,0,36.7,11.29,36.7,34.89v41.54c.02,6.42,1.61,11.32,3.35,13.29,1.65,1.87,2.95,2.36,2.95,2.36,1.23.46,1.97.23,2.77.89.85.7,1.29,1.99,1.15,2.84ZM642.5,234.61v-31.26c-2.22,2.42-5.65,4.03-12.1,6.66-8.47,3.43-17.34,7.26-17.34,20.57,0,8.67,3.23,15.33,12.1,15.33,6.66,0,12.7-3.83,17.34-11.29Z" />
      </svg>
    </div>
  );
};
Logo.displayName = "Logo";

const Heading = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="welcome-screen-center__heading welcome-screen-decor">
      {children}
    </div>
  );
};
Heading.displayName = "Heading";

const Menu = ({ children }: { children?: React.ReactNode }) => {
  return <div className="welcome-screen-menu">{children}</div>;
};
Menu.displayName = "Menu";

const MenuItemHelp = () => {
  const actionManager = useExcalidrawActionManager();

  return (
    <WelcomeScreenMenuItem
      onSelect={() => actionManager.executeAction(actionShortcuts)}
      shortcut="?"
      icon={HelpIcon}
    >
      {t("helpDialog.title")}
    </WelcomeScreenMenuItem>
  );
};
MenuItemHelp.displayName = "MenuItemHelp";

const MenuItemLoadScene = () => {
  const appState = useUIAppState();
  const actionManager = useExcalidrawActionManager();

  if (appState.viewModeEnabled) {
    return null;
  }

  return (
    <WelcomeScreenMenuItem
      onSelect={() => actionManager.executeAction(actionLoadScene)}
      shortcut={getShortcutFromShortcutName("loadScene")}
      icon={LoadIcon}
    >
      {t("buttons.load")}
    </WelcomeScreenMenuItem>
  );
};
MenuItemLoadScene.displayName = "MenuItemLoadScene";

const MenuItemLiveCollaborationTrigger = ({
  onSelect,
}: {
  onSelect: () => any;
}) => {
  const { t } = useI18n();
  return (
    <WelcomeScreenMenuItem shortcut={null} onSelect={onSelect} icon={usersIcon}>
      {t("labels.liveCollaboration")}
    </WelcomeScreenMenuItem>
  );
};
MenuItemLiveCollaborationTrigger.displayName =
  "MenuItemLiveCollaborationTrigger";

// -----------------------------------------------------------------------------

Center.Logo = Logo;
Center.Heading = Heading;
Center.Menu = Menu;
Center.MenuItem = WelcomeScreenMenuItem;
Center.MenuItemLink = WelcomeScreenMenuItemLink;
Center.MenuItemHelp = MenuItemHelp;
Center.MenuItemLoadScene = MenuItemLoadScene;
Center.MenuItemLiveCollaborationTrigger = MenuItemLiveCollaborationTrigger;

export { Center };
